import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';

import { ClaimDataRangeSelectionComponent } from './steps/range-mapping/claim-data-range-selection.component';
import { ClaimDataColumnSelectionComponent } from './steps/column-mapping/claim-data-column-selection.component';
import { ClaimsDataImportDataComponent } from './steps/import-data/claims-data-import-data.component';

import { WizardStep } from '../../../shared/controls/wizard-control/wizard-step.interface';
import { WizardStepComponent } from '../../../shared/controls/wizard-control/wizard-step.component';
import { WizardControlComponent } from '../../../shared/controls/wizard-control/wizard-control.component';

import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';

import { RenewalClaimService, API_BASE_URL } from '../../../../service/proxy';
import { Inject, Optional } from '@angular/core';

@Component({
    selector: 'app-claim-data-wizard',
    templateUrl: './claim-data-wizard.component.html',
    styleUrl: './claim-data-wizard.component.scss',

    imports: [
        CommonModule,
        FormsModule,
        WizardControlComponent,
        WizardStepComponent,
        ClaimDataRangeSelectionComponent,
        ClaimDataColumnSelectionComponent,
        ClaimsDataImportDataComponent
    ],

    providers: [
        RenewalClaimService,
    ]
})
export class ClaimDataWizardComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('rowSelectionComponent')
    rowSelectionComponent!: WizardStep;

    @ViewChild('columnSelectionComponent')
    columnSelectionComponent!: WizardStep;

    @ViewChild('importComponent')
    importComponent!: WizardStep;

    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;
    public viewInitialized: boolean = false;
    public completing: boolean = false;

    private pollingTimer: any = null;
    private baseUrl: string;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private http: HttpClient,
        private toastService: ToastService,
        private errorService: ErrorService,
        private renewalClaimService: RenewalClaimService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(API_BASE_URL) baseUrl?: string,
    ) {
        this.baseUrl = baseUrl ?? '';
    }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.batchId = +params['id'];
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.viewInitialized = true;
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    isRowSelectionValid(): boolean {
        if (!this.viewInitialized) return true;
        return this.rowSelectionComponent?.isValid() || false;
    }

    isColumnSelectionValid(): boolean {
        if (!this.viewInitialized) return true;
        return this.columnSelectionComponent?.isValid() || false;
    }

    isImportValid(): boolean {
        if (!this.viewInitialized) return true;
        return this.importComponent?.isValid() || false;
    }

    finished(): void {
        this.completing = true;
        this.renewalClaimService.completeRenewalClaimBatch(this.batchId)
            .subscribe({
                next: () => {
                    this.startPolling();
                },
                error: (error) => {
                    this.completing = false;
                    this.errorService.handleError(error);
                },
            }
        );
    }

    private startPolling(): void {
        this.pollingTimer = setInterval(() => {
            this.http.get<any>(`${this.baseUrl}/renewal-claim/${this.batchId}/completion-status`)
                .subscribe({
                    next: (result) => {
                        if (result.status === 'completed') {
                            this.stopPolling();
                            this.completing = false;
                            this.toastService.show('Renewal claim batch completed successfully.', 'Success');
                            this.router.navigate([`/claim-data`]);
                        } else if (result.status === 'failed') {
                            this.stopPolling();
                            this.completing = false;
                            this.toastService.show(`Completion failed: ${result.error}`, 'Error');
                        }
                    },
                    error: () => {
                        // Polling error — keep trying
                    }
                });
        }, 5000);
    }

    private stopPolling(): void {
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    onCancel(): void {
        this.router.navigate([`/claim-data`]);
    }
}